import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Fab, Tooltip, Button, Tabs, Tab, Paper } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

import ConversationFilters from './ConversationFilters';
import ConversationList from './ConversationList';
import ConversationDetail from './ConversationDetail';
import ExportDialog from './ExportDialog';
import BulkCloseDialog from './BulkCloseDialog';
import BulkDealReconciliationDialog from './BulkDealReconciliationDialog';
import {
  ConversationFilters as ConversationFiltersType,
  ConversationArchiveStatus,
  ConversationDeliveryBucket,
  ConversationLossReasonFilter,
  ConversationSortField,
  DeclineReason,
  SortDirection,
} from '../../services/conversationService';
import { ConversationLeadStatus, getConversationLeadStatusScope } from './conversationLeadStatus';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_ARCHIVE_STATUS: ConversationArchiveStatus = 'active';

const Conversations: React.FC = () => {
  const { hasPermission } = useAuth();
  const canExportConversations = hasPermission('export_conversations');
  const canManageConversations = hasPermission('manage_conversations');
  const [searchParams, setSearchParams] = useSearchParams();
  const fullPageConversationId = (searchParams.get('conversationId') || '').trim();
  const [panelConversationId, setPanelConversationId] = useState<string | null>(null);
  const [filters, setFilters] = useState<ConversationFiltersType>({
    archive_status: DEFAULT_ARCHIVE_STATUS,
    delivery_bucket: 'main_inbox',
  });
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [bulkCloseDialogOpen, setBulkCloseDialogOpen] = useState(false);
  const [bulkReconciliationDialogOpen, setBulkReconciliationDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<ConversationSortField | undefined>('last_activity_at');
  const [sortDirection, setSortDirection] = useState<SortDirection | undefined>('desc');
  const [selectedFunnelStatus, setSelectedFunnelStatus] = useState<ConversationLeadStatus | undefined>();
  const [selectedLossReason, setSelectedLossReason] = useState<ConversationLossReasonFilter | undefined>();
  const [selectedDeclineReason, setSelectedDeclineReason] = useState<DeclineReason | undefined>();

  const handleFiltersChange = useCallback((newFilters: ConversationFiltersType) => {
    if (!newFilters.lead_statuses?.length) {
      setSelectedFunnelStatus(undefined);
    }
    if (!newFilters.loss_reason) {
      setSelectedLossReason(undefined);
    }
    if (!newFilters.decline_reason) {
      setSelectedDeclineReason(undefined);
    }

    setFilters(newFilters);
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({ archive_status: DEFAULT_ARCHIVE_STATUS, delivery_bucket: 'main_inbox' });
    setSelectedFunnelStatus(undefined);
    setSelectedLossReason(undefined);
    setSelectedDeclineReason(undefined);
    setSearchQuery('');
  }, []);

  const handleDeliveryBucketChange = useCallback((bucket: ConversationDeliveryBucket) => {
    setFilters((previousFilters) => ({
      ...previousFilters,
      delivery_bucket: bucket,
    }));
  }, []);

  const handleLeadStatusSelect = useCallback((leadStatus?: ConversationLeadStatus) => {
    setSelectedFunnelStatus(leadStatus);
    setSelectedLossReason(undefined);
    setSelectedDeclineReason(undefined);
    setFilters((previousFilters) => {
      const nextFilters = { ...previousFilters };

      if (leadStatus) {
        nextFilters.lead_statuses = getConversationLeadStatusScope(leadStatus);
      } else {
        delete nextFilters.lead_statuses;
      }

      delete nextFilters.lead_status;
      delete nextFilters.loss_reason;
      delete nextFilters.decline_reason;

      return nextFilters;
    });
  }, []);

  const handleLossReasonSelect = useCallback((lossReason?: ConversationLossReasonFilter) => {
    setSelectedLossReason(lossReason);
    setSelectedDeclineReason(undefined);
    setSelectedFunnelStatus(lossReason ? 'lost' : undefined);
    setFilters((previousFilters) => {
      const nextFilters = { ...previousFilters };

      if (lossReason) {
        nextFilters.lead_statuses = ['lost'];
        nextFilters.loss_reason = lossReason;
      } else {
        delete nextFilters.lead_statuses;
        delete nextFilters.loss_reason;
      }

      delete nextFilters.lead_status;
      delete nextFilters.decline_reason;

      return nextFilters;
    });
  }, []);

  const handleDeclineReasonSelect = useCallback((declineReason?: DeclineReason) => {
    setSelectedDeclineReason(declineReason);
    setSelectedLossReason(undefined);
    setSelectedFunnelStatus(declineReason ? 'lost' : undefined);
    setFilters((previousFilters) => {
      const nextFilters = { ...previousFilters };

      if (declineReason) {
        nextFilters.lead_statuses = ['lost'];
        nextFilters.decline_reason = declineReason;
      } else {
        delete nextFilters.lead_statuses;
        delete nextFilters.decline_reason;
      }

      delete nextFilters.lead_status;
      delete nextFilters.loss_reason;

      return nextFilters;
    });
  }, []);

  const handleConversationSelect = useCallback((conversationId: string) => {
    setPanelConversationId(conversationId);
  }, []);

  const handleConversationOpenFullPage = useCallback((conversationId: string) => {
    setPanelConversationId(null);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('conversationId', conversationId);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleConversationOpenInNewTab = useCallback((conversationId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('conversationId', conversationId);
    window.open(`/conversations?${nextParams.toString()}`, '_blank', 'noopener,noreferrer');
  }, [searchParams]);

  const handleBackToList = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('conversationId');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleClosePanel = useCallback(() => {
    setPanelConversationId(null);
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleArchiveStateChange = useCallback((isArchived: boolean) => {
    const effectiveArchiveStatus = filters.archive_status ?? DEFAULT_ARCHIVE_STATUS;
    const archiveStateNoLongerMatchesFilter =
      (isArchived && effectiveArchiveStatus === 'active') ||
      (!isArchived && effectiveArchiveStatus === 'archived');

    setRefreshTrigger(prev => prev + 1);

    if (archiveStateNoLongerMatchesFilter) {
      setPanelConversationId(null);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('conversationId');
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters.archive_status, searchParams, setSearchParams]);

  const handleExportOpen = useCallback(() => {
    setExportDialogOpen(true);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  const handleSortChange = useCallback((nextSortBy: ConversationSortField, nextSortDirection: SortDirection) => {
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
  }, []);

  // Show conversation detail if one is selected
  if (fullPageConversationId) {
    return (
      <Box className="fade-in">
        <ConversationDetail
          conversationId={fullPageConversationId}
          onBack={handleBackToList}
          archiveStatus={filters.archive_status ?? DEFAULT_ARCHIVE_STATUS}
          onArchiveStateChange={handleArchiveStateChange}
        />
      </Box>
    );
  }

  // Show conversation list with filters
  return (
    <Box className="fade-in">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Gestión de Conversaciones
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra las conversaciones de WhatsApp con clientes
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          {canManageConversations && (
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setBulkReconciliationDialogOpen(true)}
            >
              Reconciliar deals
            </Button>
          )}
          {canManageConversations && (
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setBulkCloseDialogOpen(true)}
            >
              Cerrar desde Excel
            </Button>
          )}
          {canExportConversations && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExportOpen}
            >
              Exportar
            </Button>
          )}
          
          <Tooltip title="Actualizar conversaciones">
            <Fab
              color="primary"
              size="medium"
              onClick={handleRefresh}
              sx={{ boxShadow: 2 }}
            >
              <RefreshIcon />
            </Fab>
          </Tooltip>
        </Box>
      </Box>

      <Box mb={3}>
        <Paper sx={{ mb: 2, overflow: 'hidden' }}>
          <Tabs
            value={filters.delivery_bucket ?? 'main_inbox'}
            onChange={(_event, newValue) => handleDeliveryBucketChange(newValue as ConversationDeliveryBucket)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab value="main_inbox" label="Inbox principal" />
            <Tab value="pending" label="Pendientes de entrega" />
            <Tab value="not_delivered" label="No entregadas" />
            <Tab value="all" label="Todas" />
          </Tabs>
        </Paper>
        <ConversationFilters
          filters={filters}
          searchQuery={searchQuery}
          selectedLeadStatus={selectedFunnelStatus}
          selectedLossReason={selectedLossReason}
          selectedDeclineReason={selectedDeclineReason}
          refreshTrigger={refreshTrigger}
          onFiltersChange={handleFiltersChange}
          onSearchChange={handleSearchChange}
          onClearFilters={handleClearFilters}
          onLeadStatusSelect={handleLeadStatusSelect}
          onLossReasonSelect={handleLossReasonSelect}
          onDeclineReasonSelect={handleDeclineReasonSelect}
        />
      </Box>

      <ConversationList
        filters={filters}
        searchQuery={searchQuery}
        onConversationSelect={handleConversationSelect}
        onConversationOpenFullPage={handleConversationOpenFullPage}
        onConversationOpenInNewTab={handleConversationOpenInNewTab}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        refreshTrigger={refreshTrigger}
      />

      {panelConversationId && (
        <Paper
          component="aside"
          aria-label="Panel de detalle de conversación"
          elevation={8}
          sx={{
            position: 'fixed',
            top: { xs: 0, md: 64 },
            right: 0,
            bottom: 0,
            width: { xs: '100vw', md: 'min(1120px, calc(100vw - 360px))' },
            zIndex: (theme) => theme.zIndex.drawer + 2,
            overflowY: 'auto',
            borderRadius: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            p: { xs: 2, md: 3 },
          }}
        >
          <ConversationDetail
            conversationId={panelConversationId}
            onBack={handleClosePanel}
            archiveStatus={filters.archive_status ?? DEFAULT_ARCHIVE_STATUS}
            onArchiveStateChange={handleArchiveStateChange}
          />
        </Paper>
      )}

      <ExportDialog
        open={exportDialogOpen}
        onClose={handleExportClose}
        query={searchQuery}
        filters={{
          ...filters,
          sort_by: sortBy,
          sort_direction: sortDirection,
        }}
      />
      <BulkCloseDialog
        open={bulkCloseDialogOpen}
        onClose={() => setBulkCloseDialogOpen(false)}
        onCompleted={handleRefresh}
      />
      {bulkReconciliationDialogOpen && (
        <BulkDealReconciliationDialog
          open
          onClose={() => setBulkReconciliationDialogOpen(false)}
          onCompleted={handleRefresh}
        />
      )}
    </Box>
  );
};

export default Conversations;
